import React, { useState } from "react";
import { disconnectJobber } from "@repo/backend";
import type { ClientConfig } from "@repo/backend/schema/clientConfig";

const JOBBER_CLIENT_ID = "a26eb3d3-c670-4900-bd08-e3575957b994";
const CLIENT_ID = "maxanderson";

const PLATFORM_CALLBACK = "https://api.grateful8.us/jobber/callback";

function buildAuthUrl(): string {
  const state = JSON.stringify({ clientId: CLIENT_ID, origin: window.location.origin });
  const params = new URLSearchParams({
    response_type: "code",
    client_id: JOBBER_CLIENT_ID,
    redirect_uri: PLATFORM_CALLBACK,
    state,
  });
  return `https://api.getjobber.com/api/oauth/authorize?${params}`;
}

export function JobberAuthPage({ config }: { config?: ClientConfig }) {
  const params = new URLSearchParams(window.location.search);
  const connected = config?.jobberConnected === true || params.get("connected") === "true";
  const error = params.get("error");
  const callbackUrl = PLATFORM_CALLBACK;

  const [disconnecting, setDisconnecting] = useState(false);
  const [disconnectError, setDisconnectError] = useState("");
  const [disconnected, setDisconnected] = useState(false);

  async function handleDisconnect() {
    if (!confirm("Disconnect Jobber? Review emails will stop and leads will no longer sync to Jobber.")) return;
    setDisconnecting(true);
    setDisconnectError("");
    try {
      await disconnectJobber(CLIENT_ID);
      setDisconnected(true);
    } catch (err: unknown) {
      setDisconnectError("Disconnect failed. Check the console and try again.");
      console.error("[JobberAuth] disconnect failed:", err);
    } finally {
      setDisconnecting(false);
    }
  }

  return (
    <div style={{ minHeight: "100vh", background: "#0a0a0a", color: "#fff", fontFamily: "sans-serif", display: "flex", alignItems: "center", justifyContent: "center", padding: "2rem" }}>
      <div style={{ maxWidth: 480, width: "100%" }}>
        <p style={{ fontSize: 11, letterSpacing: "0.2em", textTransform: "uppercase", color: "#666", marginBottom: 8 }}>
          Grateful8 — Internal
        </p>
        <h1 style={{ fontSize: 28, fontWeight: 700, marginBottom: 8 }}>Jobber Integration</h1>
        <p style={{ color: "#888", marginBottom: 32, fontSize: 14, lineHeight: 1.6 }}>
          Authorizes this site to create clients in Jobber on form submissions and send review request emails when jobs are completed.
        </p>

        {disconnected && (
          <div style={{ background: "#2b1a0d", border: "1px solid #5c3a1a", borderRadius: 6, padding: "16px 20px", marginBottom: 24 }}>
            <p style={{ color: "#fb923c", fontWeight: 600, margin: 0 }}>Disconnected</p>
            <p style={{ color: "#fdba74", fontSize: 13, margin: "4px 0 0" }}>
              Jobber integration removed. Reconnect below when ready.
            </p>
          </div>
        )}

        {connected && !disconnected && (
          <div style={{ background: "#0d2b1a", border: "1px solid #1a5c38", borderRadius: 6, padding: "16px 20px", marginBottom: 24 }}>
            <p style={{ color: "#4ade80", fontWeight: 600, margin: 0 }}>Connected successfully</p>
            <p style={{ color: "#86efac", fontSize: 13, margin: "4px 0 0" }}>
              Tokens stored. Form submissions will sync to Jobber and review emails are active.
            </p>
          </div>
        )}

        {error && (
          <div style={{ background: "#2b0d0d", border: "1px solid #5c1a1a", borderRadius: 6, padding: "16px 20px", marginBottom: 24 }}>
            <p style={{ color: "#f87171", fontWeight: 600, margin: 0 }}>Connection failed</p>
            <p style={{ color: "#fca5a5", fontSize: 13, margin: "4px 0 0" }}>{error.replace(/_/g, " ")}</p>
          </div>
        )}

        {disconnectError && (
          <div style={{ background: "#2b0d0d", border: "1px solid #5c1a1a", borderRadius: 6, padding: "16px 20px", marginBottom: 24 }}>
            <p style={{ color: "#f87171", fontWeight: 600, margin: 0 }}>Disconnect failed</p>
            <p style={{ color: "#fca5a5", fontSize: 13, margin: "4px 0 0" }}>{disconnectError}</p>
          </div>
        )}

        <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginBottom: 32 }}>
          <a
            href={buildAuthUrl()}
            style={{
              display: "inline-block",
              background: "#f59e0b",
              color: "#000",
              padding: "12px 24px",
              borderRadius: 4,
              fontWeight: 700,
              fontSize: 13,
              textDecoration: "none",
              letterSpacing: "0.05em",
            }}
          >
            {connected && !disconnected ? "Reconnect to Jobber" : "Connect to Jobber"}
          </a>

          {connected && !disconnected && (
            <button
              onClick={handleDisconnect}
              disabled={disconnecting}
              style={{
                display: "inline-block",
                background: "transparent",
                color: "#ef4444",
                padding: "12px 24px",
                borderRadius: 4,
                fontWeight: 700,
                fontSize: 13,
                border: "1px solid #ef4444",
                cursor: disconnecting ? "not-allowed" : "pointer",
                letterSpacing: "0.05em",
                opacity: disconnecting ? 0.6 : 1,
              }}
            >
              {disconnecting ? "Disconnecting…" : "Disconnect"}
            </button>
          )}
        </div>

        <div style={{ borderTop: "1px solid #222", paddingTop: 24 }}>
          <p style={{ fontSize: 12, color: "#555", marginBottom: 8, textTransform: "uppercase", letterSpacing: "0.15em" }}>
            Setup — register this redirect URI in Jobber app settings
          </p>
          <code style={{ display: "block", background: "#111", border: "1px solid #333", borderRadius: 4, padding: "10px 14px", fontSize: 12, color: "#a3e635", wordBreak: "break-all" }}>
            {callbackUrl}
          </code>
        </div>
      </div>
    </div>
  );
}
